import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { subAreaApi } from '@/infrastructure/api/SubAreaApi';
import { SubAreaDto, CreateSubAreaDto, UpdateSubAreaDto } from '@/domain/entities/SubArea';
import { notificationService } from '@/infrastructure/notifications';
import { queryKeys } from './queryKeys';
import { CACHE_CONFIG } from './queryClient';
import { UserRole } from '@/domain/value-objects/UserRole';
import { RegionRepository } from '@/infrastructure/repositories/RegionRepository';
import { apiClient } from '@/infrastructure/api/ApiClient';

/**
 * Create SubArea mutation params
 */
interface CreateSubAreaParams {
  dto: CreateSubAreaDto;
  userRole: UserRole;
}

/**
 * Update SubArea mutation params
 */
interface UpdateSubAreaParams {
  subAreaId: string;
  dto: UpdateSubAreaDto;
  userRole: UserRole;
}

/**
 * Delete SubArea mutation params
 */
interface DeleteSubAreaParams {
  subAreaId: string;
  regionId: string; // Needed for cache invalidation
  userRole: UserRole;
}

/**
 * Toggle SubArea active status mutation params
 */
interface ToggleSubAreaActiveParams {
  subAreaId: string;
  regionId: string; // Needed for cache invalidation
  userRole: UserRole;
}

/**
 * Fetch SubAreas by Region query hook (admin only)
 *
 * IMPORTANT: regionId parameter can be either:
 * - GUID (from RegionSelect component): "4ee4c534-4a1c-4642-b304-08f3751e476e"
 * - Human-readable string (from backend): "seattle"
 *
 * This hook handles both cases by:
 * 1. If regionId looks like GUID → fetch Region by GUID to get regionId string
 * 2. If regionId is string → use directly
 *
 * @param regionId - The Region ID (GUID or human-readable string)
 * @param userRole - Current user's role
 * @param enabled - Whether the query should run (default: true for admins)
 */
export const useSubAreasByRegionQuery = (
  regionId: string,
  userRole?: UserRole,
  enabled: boolean = true
): UseQueryResult<SubAreaDto[], Error> => {
  // Helper to check if string is a GUID
  const isGuid = (str: string): boolean => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(str);
  };

  return useQuery({
    queryKey: queryKeys.subAreas.byRegion(regionId),
    queryFn: async (): Promise<SubAreaDto[]> => {
      let actualRegionId = regionId;

      // If regionId is a GUID, fetch the region to get the human-readable regionId
      if (isGuid(regionId)) {
        const regionRepository = new RegionRepository(apiClient);
        const regionDetail = await regionRepository.getById(regionId);
        actualRegionId = regionDetail.regionId; // Extract human-readable ID (e.g., "seattle")
      }

      // Now fetch SubAreas using the human-readable regionId
      return subAreaApi.getSubAreasByRegion(actualRegionId);
    },
    staleTime: CACHE_CONFIG.REGIONS.staleTime, // Use same cache config as regions
    gcTime: CACHE_CONFIG.REGIONS.gcTime,
    enabled: enabled && userRole === 'Admin', // Only admins can fetch SubAreas
    retry: 3, // Retry failed requests 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000) // Exponential backoff: 1s, 2s, 4s
  });
};

/**
 * Fetch single SubArea by ID query hook (admin only)
 *
 * @param subAreaId - The SubArea ID to fetch
 * @param userRole - Current user's role
 * @param enabled - Whether the query should run (default: true for admins)
 */
export const useSubAreaQuery = (
  subAreaId: string,
  userRole?: UserRole,
  enabled: boolean = true
): UseQueryResult<SubAreaDto, Error> => {
  return useQuery({
    queryKey: queryKeys.subAreas.detail(subAreaId),
    queryFn: async (): Promise<SubAreaDto> => {
      return subAreaApi.getSubAreaById(subAreaId);
    },
    staleTime: CACHE_CONFIG.REGIONS.staleTime,
    gcTime: CACHE_CONFIG.REGIONS.gcTime,
    enabled: enabled && userRole === 'Admin' && !!subAreaId
  });
};

/**
 * Create SubArea mutation hook (admin only)
 *
 * Creates a new SubArea and invalidates the SubAreas cache for the parent Region
 */
export const useCreateSubAreaMutation = (): UseMutationResult<SubAreaDto, Error, CreateSubAreaParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dto, userRole }: CreateSubAreaParams): Promise<SubAreaDto> => {
      if (userRole !== 'Admin') {
        throw new Error('Only admins can create SubAreas');
      }
      return subAreaApi.createSubArea(dto);
    },
    retry: 2, // Retry mutations twice on network failures
    retryDelay: 1000, // Wait 1 second between retries
    onSuccess: (data, variables): void => {
      // Invalidate SubAreas list for this region
      void queryClient.invalidateQueries({ queryKey: queryKeys.subAreas.byRegion(variables.dto.regionId) });

      // Also invalidate region lists (to update SubArea count)
      void queryClient.invalidateQueries({ queryKey: queryKeys.regions.lists() });

      // Show success notification
      notificationService.showSuccess(
        `SubArea "${data.name}" has been created successfully`,
        'SubArea Created'
      );
    },
    onError: (error: Error): void => {
      console.error('Create SubArea failed:', error);
      notificationService.showError(
        error.message || 'Failed to create SubArea. Please try again.',
        'Create Failed'
      );
    }
  });
};

/**
 * Update SubArea mutation hook (admin only)
 *
 * Updates an existing SubArea and invalidates related caches
 */
export const useUpdateSubAreaMutation = (): UseMutationResult<void, Error, UpdateSubAreaParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ subAreaId, dto, userRole }: UpdateSubAreaParams): Promise<void> => {
      if (userRole !== 'Admin') {
        throw new Error('Only admins can update SubAreas');
      }
      return subAreaApi.updateSubArea(subAreaId, dto);
    },
    retry: 2, // Retry mutations twice on network failures
    retryDelay: 1000, // Wait 1 second between retries
    onSuccess: (_data, variables): void => {
      // Invalidate all SubArea list queries (we don't know which region this belongs to)
      void queryClient.invalidateQueries({ queryKey: queryKeys.subAreas.lists() });

      // Invalidate the specific SubArea detail
      void queryClient.invalidateQueries({ queryKey: queryKeys.subAreas.detail(variables.subAreaId) });

      // Show success notification
      notificationService.showSuccess('SubArea has been updated successfully', 'SubArea Updated');
    },
    onError: (error: Error): void => {
      console.error('Update SubArea failed:', error);
      notificationService.showError(
        error.message || 'Failed to update SubArea. Please try again.',
        'Update Failed'
      );
    }
  });
};

/**
 * Toggle SubArea active status mutation hook (admin only)
 *
 * Toggles a SubArea's active status and invalidates related caches
 */
export const useToggleSubAreaActiveMutation = (): UseMutationResult<void, Error, ToggleSubAreaActiveParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ subAreaId, userRole }: ToggleSubAreaActiveParams): Promise<void> => {
      if (userRole !== 'Admin') {
        throw new Error('Only admins can toggle SubArea active status');
      }
      return subAreaApi.toggleSubAreaActive(subAreaId);
    },
    onSuccess: (_data, variables): void => {
      // Invalidate SubAreas list for this region
      void queryClient.invalidateQueries({ queryKey: queryKeys.subAreas.byRegion(variables.regionId) });

      // Invalidate the specific SubArea detail
      void queryClient.invalidateQueries({ queryKey: queryKeys.subAreas.detail(variables.subAreaId) });

      // Show success notification
      notificationService.showSuccess('SubArea status has been updated successfully', 'SubArea Status Updated');
    },
    onError: (error: Error): void => {
      console.error('Toggle SubArea active status failed:', error);
    }
  });
};

/**
 * Delete SubArea mutation hook (admin only)
 *
 * Permanently deletes a SubArea and removes it from cache
 *
 * IMPORTANT: Backend enforces "minimum 1 SubArea per Region" rule.
 * Will fail with 400 error if attempting to delete the last SubArea.
 */
export const useDeleteSubAreaMutation = (): UseMutationResult<void, Error, DeleteSubAreaParams, unknown> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ subAreaId, userRole }: DeleteSubAreaParams): Promise<void> => {
      if (userRole !== 'Admin') {
        throw new Error('Only admins can delete SubAreas');
      }
      return subAreaApi.deleteSubArea(subAreaId);
    },
    retry: 1, // Only retry once for delete operations (to avoid accidental double-deletes)
    retryDelay: 1000, // Wait 1 second before retry
    onSuccess: (_data, variables): void => {
      // Invalidate SubAreas list for this region
      void queryClient.invalidateQueries({ queryKey: queryKeys.subAreas.byRegion(variables.regionId) });

      // Remove the specific SubArea from cache
      void queryClient.removeQueries({ queryKey: queryKeys.subAreas.detail(variables.subAreaId) });

      // Invalidate region lists (to update SubArea count)
      void queryClient.invalidateQueries({ queryKey: queryKeys.regions.lists() });

      // Show success notification
      notificationService.showSuccess('SubArea has been deleted successfully', 'SubArea Deleted');
    },
    onError: (error: Error): void => {
      console.error('Delete SubArea failed:', error);
      // Show error notification with specific message for "last SubArea" case
      const errorMessage = error.message.includes('last SubArea') || error.message.includes('minimum')
        ? 'Cannot delete the last SubArea. Each region must have at least one SubArea.'
        : error.message || 'Failed to delete SubArea. Please try again.';

      notificationService.showError(errorMessage, 'Delete Failed');
    }
  });
};
